import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { WS_EVENTS } from '@easyedit/shared'
import { api } from '../../lib/api/client'
import type { ExportItem, JobStatus, Project } from '../../lib/api/types'
import { on } from '../../lib/ws/client'
import { useEditorStore } from '../../stores/editorStore'

const PRESETS = [
  { id: 'INSTAGRAM_REELS', label: 'Instagram Reels', size: '1080×1920' },
  { id: 'TIKTOK', label: 'TikTok', size: '1080×1920' },
  { id: 'YOUTUBE_SHORTS', label: 'YouTube Shorts', size: '1080×1920' },
  { id: 'YOUTUBE', label: 'YouTube 1080p', size: '1920×1080' },
  { id: 'INSTAGRAM_FEED', label: 'Instagram Feed', size: '1080×1350' },
]

export function ExportDialog({ project, onClose }: { project: Project; onClose: () => void }) {
  const queryClient = useQueryClient()
  const sequence = useEditorStore((s) => s.sequence)
  const [preset, setPreset] = useState('YOUTUBE')
  const [quality, setQuality] = useState('STANDARD')
  const [jobId, setJobId] = useState<string | null>(null)
  const [progress, setProgress] = useState(0)
  const [stage, setStage] = useState('')

  const exports = useQuery({
    queryKey: ['exports', project.id],
    queryFn: () => api.get<ExportItem[]>(`/projects/${project.id}/exports`),
  })

  const start = useMutation({
    mutationFn: () =>
      api.post<{ jobId: string }>(`/sequences/${sequence!.id}/exports`, { preset, quality }),
    onSuccess: (result) => {
      setJobId(result.jobId)
      setProgress(0)
      setStage('na fila')
    },
    onError: (error) =>
      useEditorStore.getState().setError(error instanceof Error ? error.message : 'Falha ao exportar'),
  })

  // O progresso chega pelo WebSocket; sem ele, um render de 40 minutos seria uma tela
  // parada indistinguível de um travamento.
  useEffect(() => {
    if (!jobId) return
    const offs = [
      on(WS_EVENTS.JOB_PROGRESS, (event) => {
        if (event.jobId !== jobId) return
        setProgress(Number(event.progress) || 0)
        setStage(String(event.stage ?? ''))
      }),
      on(WS_EVENTS.JOB_COMPLETED, (event) => {
        if (event.jobId !== jobId) return
        setProgress(100)
        setStage('concluído')
        setJobId(null)
        void queryClient.invalidateQueries({ queryKey: ['exports', project.id] })
      }),
      on(WS_EVENTS.JOB_FAILED, (event) => {
        if (event.jobId !== jobId) return
        setJobId(null)
        useEditorStore.getState().setError(`Render falhou: ${String(event.error)}`)
      }),
    ]
    return () => offs.forEach((off) => off())
  }, [jobId, project.id, queryClient])

  // Rede de segurança: se um evento se perder, o polling ainda fecha o ciclo.
  useQuery({
    queryKey: ['job', jobId],
    queryFn: () => api.get<JobStatus>(`/jobs/${jobId}`),
    enabled: Boolean(jobId),
    refetchInterval: 4000,
  })

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink-950/70 px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-md rounded-lg border border-ink-700 bg-ink-850 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-ink-750 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Exportar</h2>
          <button className="btn-ghost" onClick={onClose} aria-label="Fechar">✕</button>
        </div>

        <div className="p-4 flex flex-col gap-3">
          <div className="grid gap-1.5">
            {PRESETS.map((entry) => (
              <button
                key={entry.id}
                onClick={() => setPreset(entry.id)}
                className={`rounded border px-3 py-2 flex items-center justify-between transition-colors
                  ${preset === entry.id ? 'border-accent bg-accent-soft/25' : 'border-ink-750 hover:border-ink-600'}`}
              >
                <span className="text-xs">{entry.label}</span>
                <span className="font-mono text-2xs text-ink-500">{entry.size}</span>
              </button>
            ))}
          </div>

          <label className="flex flex-col gap-1">
            <span className="label">Qualidade</span>
            <select id="exp-quality" className="field" value={quality} onChange={(e) => setQuality(e.target.value)}>
              <option value="DRAFT">Rascunho — rápido</option>
              <option value="STANDARD">Padrão</option>
              <option value="HIGH">Alta — mais lento</option>
            </select>
          </label>

          {jobId && (
            <div>
              <div className="flex justify-between text-2xs text-ink-400 mb-1">
                <span>{stage}</span>
                <span className="tnum">{progress}%</span>
              </div>
              <div className="h-1.5 bg-ink-750 rounded overflow-hidden">
                <div className="h-full bg-accent transition-[width]" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}

          <button
            className="btn-primary justify-center py-2"
            onClick={() => start.mutate()}
            disabled={start.isPending || Boolean(jobId) || !sequence}
          >
            {jobId ? 'Renderizando…' : 'Renderizar'}
          </button>

          {exports.data && exports.data.length > 0 && (
            <div className="border-t border-ink-750 pt-3">
              <h3 className="label mb-1.5">Exportações</h3>
              <div className="flex flex-col gap-1 max-h-40 overflow-y-auto">
                {exports.data.map((item) => (
                  <div key={item.id} className="flex flex-col gap-1">
                    <a
                      href={item.url}
                      download
                      className="rounded border border-ink-750 px-2.5 py-1.5 flex items-center justify-between
                                 hover:border-ink-600 transition-colors"
                    >
                      <span className="text-2xs truncate">{item.preset}</span>
                      <span className="font-mono text-2xs text-ink-500 tnum shrink-0">
                        {item.width}×{item.height} · {(item.sizeBytes / 1048576).toFixed(1)} MB
                      </span>
                    </a>
                    {/* Um clip que não entrou no arquivo precisa ser dito AQUI, junto do
                        arquivo. Descobrir assistindo é a pior forma de descobrir. */}
                    {item.skipped && item.skipped.length > 0 && (
                      <div className="rounded border border-accent/40 bg-accent-soft/40 px-2.5 py-1.5">
                        <p className="text-2xs text-accent">
                          {item.skipped.length === 1
                            ? '1 clip ficou de fora deste arquivo:'
                            : `${item.skipped.length} clips ficaram de fora deste arquivo:`}
                        </p>
                        <ul className="mt-1 flex flex-col gap-0.5">
                          {item.skipped.map((s) => (
                            <li key={s.itemId} className="text-[10px] text-ink-300 leading-snug">
                              <span className="text-ink-200">{s.label}</span> — {s.reason}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
